import { Container, Graphics, type Sprite } from 'pixi.js';
import type { EntityId, SimEvent, SimState } from '../../sim/state';
import { BURST_AT, towardHitter } from '../boss-moments';
import type { Family, RenderContext } from '../context';
import type { EnemyPose } from '../enemies';
import { FACE_RADIUS } from '../face-kit';
import type { Frame } from '../frame';
import { add, byId, hide } from '../util';
import { ViewPool } from '../views';
import { COLUMN_AT, FOOT_AT } from './larsen-face';
import type { Point } from './cable';
import { createLarsenFloor } from './larsen-floor';
import { type Wedge, createWedge, paintWedge } from './larsen-wedge';
import { FINE, NOTE_DRAWN, paintLarsenLooks } from './larsen-looks';
import {
  CABLE,
  EXIT_BEATS,
  SHARDS,
  SPARKS,
  WAVE,
  cableSag,
  cableWhip,
  columnLight,
  exitRing,
  noteRise,
  shard,
  shardRing,
  spark,
} from './larsen-moments';
import { onMaskPoint, placeOnMask } from './on-mask';

const KIND = 'larsen';
const CADENCE = { cycleBeats: 8, openBeats: 4 };
const MOUTH_Y = 0.15;
const HIT_Y = -0.2;
const SHARD_RIM = 1.2;
const SPARK = { size: 0.075, width: 2 };
const NOTE_HEIGHT = 0.6;
const MAX_HITS = 8;
const WHITE = 0xffffff;

interface Boss {
  readonly light: Sprite;
  readonly calmLight: Sprite;
}

interface Hit extends Point {
  readonly start: number;
  readonly radius: number;
  readonly toward: number;
  readonly gold: boolean;
  readonly seed: number;
}

// Where its cable ends: the floor monitor laid where it came in, facing the stage.
interface Plugged {
  readonly plug: Point;
  readonly facing: number;
  readonly wedge: Wedge;
}

interface Exit extends Point {
  readonly start: number;
  readonly radius: number;
  readonly plugged: Plugged;
  readonly note: Sprite;
}

export function createLarsen(
  ctx: RenderContext,
  poseOf: (id: EntityId) => EnemyPose | undefined,
): Family {
  const { layers } = ctx;
  const looks = paintLarsenLooks();
  const wedgeLook = paintWedge();
  const effect = ctx.specials.get(KIND);
  const cadence = effect?.kind === 'beatArmor' ? effect : CADENCE;
  const floor = createLarsenFloor(ctx, cadence);
  const props = layers.enemies.addChildAt(new Container(), 0);
  const lights = layers.enemies.addChild(new Container());
  const fx = layers.fx.addChild(new Graphics());
  const notes = layers.fx.addChild(new Container());
  const plugs = new Map<EntityId, Plugged>();
  const hits: Hit[] = [];
  const exits: Exit[] = [];
  const spareNotes: Sprite[] = [];
  const bosses = new ViewPool<Boss>(
    () => ({ light: add(lights, looks.light), calmLight: add(lights, looks.calmLight) }),
    (boss) => {
      hide(boss.light, boss.calmLight);
    },
  );

  function radiusOf(id: EntityId): number {
    return poseOf(id)?.radius ?? ctx.enemyRadii?.get(KIND) ?? FACE_RADIUS;
  }

  function plugIn(id: EntityId, plug: Point, toward: Point): Plugged {
    const facing = Math.atan2(toward.y - plug.y, toward.x - plug.x);
    const plugged = { plug, facing, wedge: createWedge(props, wedgeLook) };
    plugs.set(id, plugged);
    return plugged;
  }

  function dress(boss: Boss, id: EntityId, pose: EnemyPose, beats: number, frame: Frame): void {
    const { x, y, radius } = pose;
    const { alpha } = pose.sprite;
    const { plug, facing, wedge } =
      plugs.get(id) ?? plugIn(id, { x: x - 3 * radius, y: y + radius }, pose);
    wedge.place(plug, facing, radius, alpha);
    wedge.play(beats, cadence, frame, frame.calm);
    floor.cable(
      onMaskPoint(pose, FOOT_AT),
      wedge.socket,
      radius,
      cableSag(beats, frame.calm),
      alpha,
      frame,
    );
    floor.waves({ x, y: y + MOUTH_Y * radius }, radius, beats, beats, frame.calm, alpha, frame);
    floor.ring(pose, radius, beats, alpha, frame);
    const light = columnLight(beats, cadence);
    const [shown, hidden] = frame.calm
      ? [boss.calmLight, boss.light]
      : [boss.light, boss.calmLight];
    hidden.visible = false;
    placeOnMask(shown, pose, lights, COLUMN_AT, FINE);
    shown.scale.x *= 0.25 + 0.75 * light;
    shown.alpha = light * alpha;
    shown.visible = light > 0;
  }

  function burst(hit: Hit, frame: Frame): boolean {
    const since = (frame.now - hit.start) / frame.tempo.ticksPerBeat;
    const { x, y, radius, seed } = hit;
    const { palette } = frame;
    const corner = (cx: number, cy: number, turn: number, px: number, py: number) =>
      [
        cx + px * Math.cos(turn) - py * Math.sin(turn),
        cy + px * Math.sin(turn) + py * Math.cos(turn),
      ] as const;
    if (!hit.gold) {
      const p = since / SPARKS.beats;
      if (p >= 1) {
        return false;
      }
      for (let index = 0; index < SPARKS.count; index += 1) {
        const bit = spark(index, p, seed, hit.toward);
        const [cx, cy, size] = [x + bit.x * radius, y + bit.y * radius, SPARK.size * radius];
        fx.moveTo(...corner(cx, cy, bit.turn, -size, -size))
          .lineTo(...corner(cx, cy, bit.turn, size * 0.66, 0))
          .lineTo(...corner(cx, cy, bit.turn, -size, size))
          .stroke({
            width: SPARK.width,
            color: WHITE,
            alpha: bit.alpha,
            cap: 'round',
            join: 'round',
          });
      }
      return true;
    }
    const p = since / SHARDS.beats;
    if (p >= 1) {
      return false;
    }
    const ring = shardRing(since);
    if (ring !== undefined) {
      fx.circle(x, y, ring.radius * radius).stroke({
        width: 3,
        color: palette.noyau,
        alpha: ring.alpha,
      });
    }
    const count = frame.calm ? SHARDS.calmCount : SHARDS.count;
    for (let index = 0; index < count; index += 1) {
      const bit = shard(index, p, seed);
      const [cx, cy, size] = [x + bit.x * radius, y + bit.y * radius, bit.size * radius];
      fx.moveTo(...corner(cx, cy, bit.turn, -size, -size * 0.6))
        .lineTo(...corner(cx, cy, bit.turn, size, -size * 0.2))
        .lineTo(...corner(cx, cy, bit.turn, -size * 0.2, size))
        .closePath()
        .fill({ color: palette.noyau, alpha: bit.alpha })
        .stroke({ width: SHARD_RIM, color: palette.badVibeRim, alpha: bit.alpha });
    }
    return true;
  }

  function leave(exit: Exit, frame: Frame): boolean {
    const { ticksPerBeat } = frame.tempo;
    const beats = (frame.show - exit.start) / ticksPerBeat;
    const p = (beats - BURST_AT) / EXIT_BEATS;
    if (p >= 1) {
      return false;
    }
    const { x, y, radius, plugged, note } = exit;
    const { plug, facing, wedge } = plugged;
    const died = exit.start / ticksPerBeat;
    const fade = Math.max(0, 1 - beats / WAVE.calmBeats);
    floor.waves({ x, y: y + MOUTH_Y * radius }, radius, died, died + beats, true, fade, frame);
    const foot = { x, y: y + (FOOT_AT[1] / FACE_RADIUS) * radius };
    wedge.place(plug, facing, radius, p < 0 ? 1 : 1 - p);
    wedge.play(died, cadence, frame, true);
    const socket = wedge.socket;
    if (p < 0) {
      floor.cable(foot, socket, radius, CABLE.sag, 1, frame);
      note.visible = false;
      return true;
    }
    const whip = cableWhip(p);
    const end = {
      x: foot.x + (socket.x - foot.x) * whip.pull,
      y: foot.y + (socket.y - foot.y) * whip.pull,
    };
    floor.cable(end, socket, radius, CABLE.sag * (1 + whip.lash), whip.alpha, frame);
    const ring = exitRing(p);
    fx.circle(x, y + HIT_Y * radius, ring.radius * radius).stroke({
      width: 2,
      color: frame.palette.noyau,
      alpha: ring.alpha,
    });
    const rise = noteRise(p);
    note.visible = true;
    note.position.set(x, y + rise.y * radius);
    note.alpha = rise.alpha;
    note.scale.set((NOTE_HEIGHT * radius) / NOTE_DRAWN / FINE);
    return true;
  }

  return {
    update(state: SimState, _alpha: number, frame: Frame): void {
      floor.clear();
      fx.clear();
      bosses.begin();
      const beats = frame.now / frame.tempo.ticksPerBeat;
      for (const enemy of state.enemies) {
        const pose = enemy.kind === KIND ? poseOf(enemy.id) : undefined;
        if (pose !== undefined) {
          dress(bosses.acquire(enemy.id), enemy.id, pose, beats, frame);
        }
      }
      bosses.end();
      for (let index = hits.length - 1; index >= 0; index -= 1) {
        const hit = hits[index];
        if (hit !== undefined && !burst(hit, frame)) {
          hits.splice(index, 1);
        }
      }
      for (let index = exits.length - 1; index >= 0; index -= 1) {
        const exit = exits[index];
        if (exit !== undefined && !leave(exit, frame)) {
          exit.note.visible = false;
          spareNotes.push(exit.note);
          exit.plugged.wedge.destroy();
          exits.splice(index, 1);
        }
      }
    },
    onEvent(event: SimEvent, state: SimState, frame: Frame): void {
      if (event.type === 'enemySpawned' && event.kind === KIND) {
        plugIn(event.id, { x: event.x, y: event.y }, state.core);
      } else if (event.type === 'enemyHit' && byId(state.enemies, event.id)?.kind === KIND) {
        const radius = radiusOf(event.id);
        if (hits.length >= MAX_HITS) {
          hits.shift();
        }
        hits.push({
          start: frame.now,
          x: event.x,
          y: event.y + HIT_Y * radius,
          radius,
          toward: towardHitter(event, state),
          gold: event.armored !== true,
          seed: state.tick + hits.length,
        });
      } else if (event.type === 'enemyDied' && event.kind === KIND) {
        const radius = radiusOf(event.id);
        const plugged =
          plugs.get(event.id) ??
          plugIn(event.id, { x: event.x - 3 * radius, y: event.y + radius }, event);
        plugs.delete(event.id);
        exits.push({
          start: frame.show,
          x: event.x,
          y: event.y,
          radius,
          plugged,
          note: spareNotes.pop() ?? add(notes, looks.note),
        });
      }
    },
    reset(): void {
      bosses.releaseAll();
      for (const { wedge } of [...plugs.values(), ...exits.map((exit) => exit.plugged)]) {
        wedge.destroy();
      }
      plugs.clear();
      hits.length = 0;
      for (const exit of exits) {
        exit.note.visible = false;
        spareNotes.push(exit.note);
      }
      exits.length = 0;
      floor.clear();
      fx.clear();
    },
    destroy(): void {
      floor.destroy();
      for (const container of [props, lights, fx, notes]) {
        container.destroy({ children: true });
      }
      for (const look of [looks.light, looks.calmLight, looks.note, wedgeLook]) {
        look.texture.destroy(true);
      }
    },
  };
}
